import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { AppointmentInvoiceBar } from '../moloni/IssueInvoiceDialog';
import useBrandPalette from '../../theme/useBrandPalette';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  CalendarMonth as CalendarMonthIcon,
  CardMembership as MembershipIcon,
  Close as CloseIcon,
  ContentCut as ContentCutIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  EventBusy as EventBusyIcon,
  MoreVert as MoreVertIcon,
  Notes as NotesIcon,
  Payments as PaymentsIcon,
  Person as PersonIcon,
  ShoppingBagOutlined as ShoppingBagIcon,
  PersonOff as PersonOffIcon,
  Redeem as ServicePackageIcon,
  Badge as BadgeIcon,
  SwapHoriz as SwapHorizIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { formatServicePriceLabel } from '../../utils/serviceValueFormat';
import SetAppointmentServicePriceDialog from './SetAppointmentServicePriceDialog';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { getLocalizedValue } from '../../constants/Languages';
import { getAppointmentStatusKey, getPaymentStatusKey } from '../../utils/appointmentStatusUtils';
import { getStorePaymentMethodKey } from '../../constants/StorePaymentMethod';
import StoreCustomerService from '../../services/storeCustomerService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { formatProductSaleItemLabel } from '../../utils/productSaleItemsUtils';
import AppointmentConsentStatus from '../consent-forms/AppointmentConsentStatus';
import {
  getAppointmentProductLines,
  getAppointmentProductsTotal,
  getPendingAppointmentProductLines,
} from '../../utils/appointmentCheckoutUtils';

const statusThemes = {
  pending: { main: '#d97706', soft: '#fef3c7' },
  cancelled: { main: '#64748b', soft: '#f1f5f9' },
  confirmed: { main: '#2563eb', soft: '#dbeafe' },
  completed: { main: '#2e7d32', soft: '#e8f5e9' },
  noShow: { main: '#e11d48', soft: '#ffe4e6' },
  unknown: { main: '#6b7280', soft: '#f3f4f6' },
};

const paymentThemes = {
  paid: { main: '#2e7d32', soft: '#e8f5e9' },
  pendingPayment: { main: '#ef6c00', soft: '#fff3e0' },
  refunded: { main: '#0369a1', soft: '#e0f2fe' },
  partiallyRefunded: { main: '#0369a1', soft: '#e0f2fe' },
  disputed: { main: '#c62828', soft: '#ffebee' },
  unpaid: { main: '#6b7280', soft: '#f3f4f6' },
};

/** Brand-accent status colours (awaiting payment / deposit paid) need concrete hex for alpha(). */
const getBrandStatusTheme = (brand) => ({ main: brand.accentText, soft: alpha(brand.accent, 0.12) });

const brandHeaderChipSx = {
  height: 28,
  fontWeight: 700,
  bgcolor: alpha('#fff', 0.94),
  color: BRAND.accentText,
  border: `1px solid ${brandAlpha('accent', 0.35)}`,
  '& .MuiChip-icon': { color: BRAND.accentText },
};

const successActionButtonSx = {
  background: '#2e7d32',
  boxShadow: 'none',
  '&:hover': {
    background: '#1b5e20',
    boxShadow: 'none',
  },
};

const footerActionButtonSx = {
  minHeight: 48,
  borderRadius: BRAND_RADIUS.button,
  fontWeight: 700,
  textTransform: 'none',
};

const overflowMenuItemSx = {
  whiteSpace: 'normal',
  alignItems: 'flex-start',
  py: 1.25,
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    mt: 0.35,
  },
};

function getInitials(name) {
  if (!name || typeof name !== 'string') return '?';

  const parts = name.split(' ').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getTeamMemberPicture(teamMember) {
  return teamMember?.picture || teamMember?.Picture || null;
}

function formatAppointmentDate(dateValue, locale) {
  if (!dateValue) return '';

  const [year, month, day] = String(dateValue).split('-').map(Number);
  if (!year || !month || !day) return dateValue;

  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function formatTimeRange(period) {
  if (!period?.from || !period?.to) return '—';
  return `${period.from.slice(0, 5)} – ${period.to.slice(0, 5)}`;
}

function DetailSection({ icon: Icon, title, children }) {
  return (
    <Box
      sx={{
        borderRadius: BRAND_RADIUS.card,
        border: `1px solid ${BRAND.line}`,
        bgcolor: BRAND.surface,
        p: 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: brandAlpha('accent', 0.12),
            color: BRAND.accentText,
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.text, letterSpacing: '0.02em' }}>
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

function hasCustomerProfileId(customerId) {
  if (!customerId) {
    return false;
  }

  return String(customerId).toLowerCase() !== '00000000-0000-0000-0000-000000000000';
}

function hasConsultationNotes(overlay) {
  return Boolean(
    overlay?.allergiesAndSensitivities
    || overlay?.medicalConditions
    || overlay?.medications
    || overlay?.pregnancyNursingStatus,
  );
}

function PersonCard({ name, picture, subtitle, avatarColor = BRAND.accentText, onNameClick, nameAriaLabel }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar
        src={picture || undefined}
        alt={name}
        sx={{
          width: 52,
          height: 52,
          bgcolor: brandAlpha('accent', 0.12),
          color: avatarColor,
          fontWeight: 800,
          border: `2px solid ${BRAND.surface}`,
          boxShadow: 'none',
          flexShrink: 0,
        }}
      >
        {getInitials(name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h6"
          component={onNameClick ? 'button' : 'h6'}
          type={onNameClick ? 'button' : undefined}
          onClick={onNameClick}
          aria-label={onNameClick ? nameAriaLabel : undefined}
          sx={{
            fontWeight: 700,
            color: BRAND.text,
            lineHeight: 1.2,
            maxWidth: '100%',
            overflowWrap: 'anywhere',
            ...(onNameClick ? {
              cursor: 'pointer',
              background: 'none',
              border: 0,
              padding: 0,
              font: 'inherit',
              textAlign: 'left',
              '&:hover': {
                color: BRAND.accentText,
                textDecoration: 'underline',
              },
            } : {}),
          }}
        >
          {name}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: BRAND.textMuted, mt: 0.35 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

const AppointmentDetailsDrawer = ({
  open,
  onClose,
  appointment,
  languageCode,
  storeLanguage,
  teamMembers = [],
  currencyCode = DEFAULT_CURRENCY,
  resolveServiceName,
  canMarkPaid,
  canUpdateStatus,
  canEdit,
  canDelete,
  canChangeToRegular,
  onMarkPaid,
  canIssueInvoice = false,
  onMarkCompleted,
  onMarkCancelled,
  onMarkNoShow,
  onConfirmOnRequest,
  onDeclineOnRequest,
  onEdit,
  onDelete,
  onChangeToRegular,
  markingPaid,
  updatingStatus,
  changingToRegular,
  storeId,
  canViewCustomer = false,
  onCustomerClick,
  hideProfessional = false,
  hideProducts = false,
  canSetServicePrice = false,
  onSetServicePrice,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const brand = useBrandPalette();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const locale = languageCode === 'pt' ? 'pt-PT' : languageCode === 'es' ? 'es-ES' : 'en-US';
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [consultation, setConsultation] = useState(null);
  const [consultationLoading, setConsultationLoading] = useState(false);
  const [pricingService, setPricingService] = useState(null);

  const statusKey = appointment ? getAppointmentStatusKey(appointment.status) : 'unknown';
  const awaitingConfirmation = Boolean(appointment?.awaitingConfirmation);
  const openToAnyProfessional = Boolean(appointment?.openToAnyProfessional);
  const statusChipKey = awaitingConfirmation ? 'awaitingConfirmation' : statusKey;
  const paymentKey = appointment ? getPaymentStatusKey(appointment.paymentStatus) : 'unpaid';
  const brandStatusTheme = getBrandStatusTheme(brand);
  const statusTheme = (!awaitingConfirmation && statusKey === 'awaitingPayment')
    ? brandStatusTheme
    : (statusThemes[awaitingConfirmation ? 'pending' : statusKey] || statusThemes.unknown);
  const paymentTheme = paymentKey === 'depositPaid'
    ? brandStatusTheme
    : (paymentThemes[paymentKey] || paymentThemes.unpaid);

  const period = appointment?.displayPeriod || appointment?.period;
  const services = useMemo(() => appointment?.services || [], [appointment?.services]);
  const coveredByMembership = Boolean(appointment?.coveredByMembership);
  const pendingMembershipEnrollment = Boolean(appointment?.pendingMembershipEnrollment);
  const isMembershipRelated = coveredByMembership || pendingMembershipEnrollment;
  const coveredByServicePackage = Boolean(appointment?.coveredByServicePackage);
  const amountDue = Number(
    appointment?.amountDue
    ?? appointment?.totalPrice
    ?? 0,
  );
  const additionalCostAmount = Number(appointment?.additionalCostAmount || 0);
  const productLines = getAppointmentProductLines(appointment);
  const pendingProductsDue = getAppointmentProductsTotal(getPendingAppointmentProductLines(appointment));
  const paymentLines = useMemo(() => {
    const fromApi = (appointment?.payments || []).filter((line) => line?.paymentMethod);
    if (fromApi.length > 0) {
      return fromApi;
    }

    if (appointment?.storePaymentMethod) {
      return [{
        paymentMethod: appointment.storePaymentMethod,
        amount: Number(appointment.amountReceived ?? 0),
      }];
    }

    return [];
  }, [appointment?.payments, appointment?.storePaymentMethod, appointment?.amountReceived]);
  const amountPaid = paymentLines.length > 0
    ? paymentLines.reduce((total, line) => total + Number(line.amount || 0), 0)
    : Number(appointment?.amountReceived ?? 0);
  const isPaid = paymentKey === 'paid';
  // Refunded or disputed money was collected first: there is nothing left to take in store,
  // so the headline is what was paid, not the full price as if still owed.
  const wasCollected = isPaid || ['refunded', 'partiallyRefunded', 'disputed'].includes(paymentKey);
  const isDepositPaid = paymentKey === 'depositPaid';
  const outstandingDue = amountDue + pendingProductsDue;
  // A free booking stays unpaid until it is completed, so staff can still add a charge.
  // Until then there is no payment state worth showing.
  const nothingToCollect = outstandingDue <= 0 && !wasCollected && !isDepositPaid;
  const onlinePrepaidAmount = Number(appointment?.onlinePrepaidAmount || 0);
  const depositAmount = Number(appointment?.depositAmount || 0);
  const onlineProductAmount = isPaid ? Number(appointment?.onlineProductAmount || 0) : 0;
  const membershipPlanLabel = getLocalizedValue(
    appointment?.membershipPlanName,
    languageCode,
    storeLanguage,
    '',
  );
  const servicePackageLabel = getLocalizedValue(
    appointment?.servicePackageName,
    languageCode,
    storeLanguage,
    '',
  );
  const teamMemberById = useMemo(
    () => Object.fromEntries((teamMembers || []).map((member) => [member.id, member])),
    [teamMembers],
  );

  const professionals = useMemo(() => {
    const grouped = new Map();

    services.forEach((service) => {
      const memberId = service.teamMemberId;
      if (!grouped.has(memberId)) {
        const teamMember = teamMemberById[memberId];
        grouped.set(memberId, {
          id: memberId,
          name: service.teamMemberName
            || teamMember?.name
            || (openToAnyProfessional && awaitingConfirmation
              ? t('managementAppointments.openToAnyProfessional')
              : t('managementAppointments.unnamedProfessional')),
          picture: getTeamMemberPicture(teamMember),
          services: [],
        });
      }

      grouped.get(memberId).services.push(service);
    });

    return [...grouped.values()];
  }, [services, teamMemberById, t, openToAnyProfessional, awaitingConfirmation]);

  const customerNotes = appointment?.detailsFromCustomer?.trim();
  const showOnRequestActions = Boolean(canUpdateStatus && awaitingConfirmation && (onConfirmOnRequest || onDeclineOnRequest));
  const showMarkPaid = canMarkPaid
    && !nothingToCollect
    && !awaitingConfirmation
    && !['paid', 'refunded', 'partiallyRefunded', 'disputed'].includes(paymentKey)
    && !pendingMembershipEnrollment;
  const showStatusActions = canUpdateStatus && !awaitingConfirmation && (statusKey === 'confirmed' || statusKey === 'pending');
  const showEdit = canEdit && !awaitingConfirmation && (statusKey === 'confirmed' || statusKey === 'pending');
  const showChangeToRegular = Boolean(canChangeToRegular) && showEdit
    && (isMembershipRelated || coveredByServicePackage);
  const showDelete = canDelete && statusKey !== 'completed';
  const showCancel = showStatusActions || showOnRequestActions;
  const showNoShow = showStatusActions;
  const hasOverflowActions = showEdit || showChangeToRegular || showMarkPaid || showCancel || showNoShow || showDelete;
  const isBusy = markingPaid || updatingStatus || changingToRegular;
  const isMenuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleMenuAction = (action) => {
    handleMenuClose();
    action?.();
  };

  useEffect(() => {
    if (!open) {
      setMenuAnchor(null);
    }
  }, [open]);

  const customerId = appointment?.customerId;
  const canOpenCustomer = Boolean(canViewCustomer && hasCustomerProfileId(customerId) && onCustomerClick);

  useEffect(() => {
    if (!open || !storeId || !canViewCustomer || !hasCustomerProfileId(customerId)) {
      setConsultation(null);
      setConsultationLoading(false);
      return undefined;
    }

    let cancelled = false;
    setConsultationLoading(true);

    requestDeduplicator
      .deduplicate(
        `appointment-customer-consultation-${storeId}-${customerId}`,
        () => StoreCustomerService.getStoreCustomer(storeId, customerId),
      )
      .then((data) => {
        if (!cancelled) {
          setConsultation(data?.overlay || {});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConsultation({});
        }
      })
      .finally(() => {
        if (!cancelled) {
          setConsultationLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, storeId, customerId, canViewCustomer]);

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      disableScrollLock
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : { sm: 420, md: 460 },
          maxWidth: '100vw',
          height: isMobile ? 'min(92dvh, 820px)' : '100%',
          maxHeight: isMobile ? '92dvh' : '100%',
          borderTopLeftRadius: isMobile ? 20 : 0,
          borderTopRightRadius: isMobile ? 20 : 0,
          overflow: 'hidden',
          background: BRAND.surface,
          boxShadow: isMobile
            ? `0 -12px 40px ${brandAlpha('primary', 0.16)}`
            : `0 0 40px ${brandAlpha('primary', 0.12)}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            p: { xs: 2.25, sm: 3 },
            background: BRAND.primary,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label={t('common.close')}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: BRAND.onPrimary,
              bgcolor: alpha('#000', 0.12),
              '&:hover': { bgcolor: alpha('#000', 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>

          {appointment && (
            <Box sx={{ pr: 5 }}>
              <Typography
                variant="overline"
                sx={{ color: BRAND.onPrimaryMuted, letterSpacing: '0.14em', fontWeight: 700 }}
              >
                {t('managementAppointments.detailsTitle')}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: BRAND.onPrimary,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  fontSize: { xs: '1.65rem', sm: '2rem' },
                  mt: 0.25,
                }}
              >
                {formatTimeRange(period)}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, color: BRAND.onPrimaryMuted }}>
                <CalendarMonthIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatAppointmentDate(appointment.date, locale)}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Chip
                  label={t(`customer.appointments.status.${statusChipKey}`)}
                  size="small"
                  sx={{
                    height: 28,
                    fontWeight: 700,
                    bgcolor: alpha('#fff', 0.94),
                    color: statusTheme.main,
                    border: `1px solid ${alpha(statusTheme.main, 0.22)}`,
                  }}
                />
                {!nothingToCollect && (
                  <Chip
                    label={t(`managementAppointments.paymentStatus.${paymentKey}`)}
                    size="small"
                    sx={{
                      height: 28,
                      fontWeight: 700,
                      bgcolor: paymentTheme.soft,
                      color: paymentTheme.main,
                      border: `1px solid ${alpha(paymentTheme.main, 0.22)}`,
                    }}
                  />
                )}
                {openToAnyProfessional && awaitingConfirmation && (
                  <Chip
                    label={t('managementAppointments.openToAnyProfessional')}
                    size="small"
                    sx={brandHeaderChipSx}
                  />
                )}
                {coveredByMembership && (
                  <Chip
                    icon={<MembershipIcon sx={{ fontSize: '16px !important' }} />}
                    label={membershipPlanLabel || t('managementAppointments.coveredByMembership')}
                    size="small"
                    sx={brandHeaderChipSx}
                  />
                )}
                {coveredByServicePackage && (
                  <Chip
                    id="appointment-details-service-package-chip"
                    icon={<ServicePackageIcon sx={{ fontSize: '16px !important' }} />}
                    label={servicePackageLabel
                      ? t('servicePackagesManagement.appointment.packageLabel', { name: servicePackageLabel })
                      : t('servicePackagesManagement.appointment.coveredByPackage')}
                    size="small"
                    sx={brandHeaderChipSx}
                  />
                )}
                {pendingMembershipEnrollment && (
                  <Chip
                    icon={<MembershipIcon sx={{ fontSize: '16px !important' }} />}
                    label={membershipPlanLabel || t('managementAppointments.pendingMembershipEnrollment')}
                    size="small"
                    sx={{
                      ...brandHeaderChipSx,
                      border: `1px solid ${brandAlpha('accentDark', 0.35)}`,
                    }}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            px: { xs: 2, sm: 3 },
            py: { xs: 2.25, sm: 3 },
          }}
        >
          {appointment && (
            <Stack spacing={2}>
              {appointment.membershipRenewalRisk && (
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon fontSize="inherit" />}
                >
                  {t('managementAppointments.membershipRenewalRisk', {
                    date: appointment.membershipPeriodEndUtc
                      ? formatUtcDate(appointment.membershipPeriodEndUtc)
                      : '—',
                  })}
                </Alert>
              )}

              <DetailSection icon={PersonIcon} title={t('managementAppointments.customer')}>
                <PersonCard
                  name={appointment.customerName}
                  picture={appointment.customerPicture}
                  onNameClick={canOpenCustomer ? onCustomerClick : undefined}
                  nameAriaLabel={t('managementAppointments.openCustomerDetails')}
                />
                {canViewCustomer && hasCustomerProfileId(customerId) ? (
                  <Box sx={{ mt: 1.75, pt: 1.5, borderTop: `1px solid ${BRAND.line}` }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.text, mb: 1 }}>
                      {t('customersManagement.details.consultation.title')}
                    </Typography>
                    {consultationLoading ? (
                      <CircularProgress size={18} sx={{ color: BRAND.accent }} />
                    ) : hasConsultationNotes(consultation) ? (
                      <Stack spacing={1.25}>
                        {[
                          ['allergies', consultation.allergiesAndSensitivities],
                          ['medical', consultation.medicalConditions],
                          ['medications', consultation.medications],
                          ['pregnancy', consultation.pregnancyNursingStatus],
                        ].filter(([, value]) => value).map(([key, value]) => (
                          <Box key={key}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: BRAND.textMuted, display: 'block' }}>
                              {t(`customersManagement.details.consultation.${key}`)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.textMuted, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                              {value}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ color: BRAND.textSubtle, fontStyle: 'italic', lineHeight: 1.6 }}>
                        {t('managementAppointments.noConsultationNotes')}
                      </Typography>
                    )}
                  </Box>
                ) : null}
                {hasCustomerProfileId(customerId) ? (
                  <AppointmentConsentStatus
                    storeId={storeId}
                    appointmentId={appointment.id}
                    customerId={customerId}
                  />
                ) : null}
              </DetailSection>

              {!hideProfessional && (
              <DetailSection
                icon={BadgeIcon}
                title={professionals.length > 1
                  ? t('managementAppointments.professionals')
                  : t('managementAppointments.professional')}
              >
                <Stack spacing={1.5} divider={professionals.length > 1 ? <Divider flexItem sx={{ borderColor: BRAND.lineSoft }} /> : null}>
                  {professionals.map((professional) => (
                    <Box key={professional.id}>
                      <PersonCard
                        name={professional.name}
                        picture={professional.picture}
                        avatarColor="#1d4ed8"
                      />
                      {professionals.length > 1 && (
                        <Stack spacing={0.75} sx={{ mt: 1.25, pl: 7.5 }}>
                          {professional.services.map((service) => (
                            <Typography key={`${professional.id}-${service.serviceId}`} variant="body2" sx={{ color: BRAND.textMuted }}>
                              {resolveServiceName(service.name)}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
                </Stack>
              </DetailSection>
              )}

              <DetailSection
                icon={ContentCutIcon}
                title={services.length === 1
                  ? t('managementAppointments.service')
                  : t('managementAppointments.services')}
              >
                <Stack spacing={1} divider={services.length > 1 ? <Divider flexItem sx={{ borderColor: BRAND.lineSoft }} /> : null}>
                  {services.map((service) => (
                    <Stack
                      key={`${appointment.id}-${service.serviceId}-${service.teamMemberId}`}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={1}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: BRAND.text }}>
                          {resolveServiceName(service.name)}
                        </Typography>
                        {professionals.length > 1 && (
                          <Typography variant="body2" sx={{ color: BRAND.textMuted, mt: 0.35 }}>
                            {service.teamMemberName}
                          </Typography>
                        )}
                      </Box>
                      {service.unitPrice == null ? (
                        canSetServicePrice ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setPricingService(service)}
                            sx={{
                              flexShrink: 0,
                              color: BRAND.accentText,
                              borderColor: BRAND.line,
                              fontWeight: 700,
                              textTransform: 'none',
                            }}
                          >
                            {t('managementAppointments.setPrice.action')}
                          </Button>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.primaryText, whiteSpace: 'nowrap' }}>
                            {formatServicePriceLabel(
                              {
                                priceMode: service.priceMode,
                                price: service.catalogPrice,
                                priceMax: service.priceMax,
                              },
                              {
                                formatMoney: (amount) => formatCurrency(amount, currencyCode),
                                onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                                fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
                                freeLabel: t('customer.appointments.booking.priceToConfirm'),
                              },
                            )}
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.primaryText, whiteSpace: 'nowrap' }}>
                          {formatCurrency(Number(service.unitPrice), currencyCode)}
                        </Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </DetailSection>

              {!hideProducts && productLines.length > 0 && (
                <DetailSection
                  icon={ShoppingBagIcon}
                  title={productLines.length === 1
                    ? t('managementAppointments.product')
                    : t('managementAppointments.products')}
                >
                  <Stack spacing={1} divider={productLines.length > 1 ? <Divider flexItem sx={{ borderColor: BRAND.lineSoft }} /> : null}>
                    {productLines.map((item) => (
                      <Stack
                        key={`${appointment.id}-${item.productId}-${item.quantity}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 700, color: BRAND.text }}>
                          {formatProductSaleItemLabel(item, languageCode)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.primaryText, whiteSpace: 'nowrap' }}>
                          {formatCurrency(Number(item.lineTotal) || 0, currencyCode)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </DetailSection>
              )}

              {(outstandingDue > 0 || amountPaid > 0 || onlinePrepaidAmount > 0 || depositAmount > 0 || (isPaid && canIssueInvoice)) && (
              <DetailSection
                icon={PaymentsIcon}
                title={t(wasCollected ? 'managementAppointments.amountPaid' : 'managementAppointments.amountDue')}
              >
                <Stack spacing={0.5}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: BRAND.primaryText, textAlign: 'right' }}>
                    {formatCurrency(wasCollected ? amountPaid : (amountDue + pendingProductsDue), currencyCode)}
                  </Typography>
                  {isDepositPaid && onlinePrepaidAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                        {t('managementAppointments.prepayment.paidOnline')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: BRAND.text }}>
                        {formatCurrency(onlinePrepaidAmount, currencyCode)}
                      </Typography>
                    </Stack>
                  )}
                  {onlineProductAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                        {t('managementAppointments.prepayment.productsPaidOnline')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: BRAND.text }}>
                        {formatCurrency(onlineProductAmount, currencyCode)}
                      </Typography>
                    </Stack>
                  )}
                  {depositAmount > 0 && (isDepositPaid || isPaid) && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                        {t('managementAppointments.prepayment.nonRefundableDeposit')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: BRAND.primaryText }}>
                        {formatCurrency(depositAmount, currencyCode)}
                      </Typography>
                    </Stack>
                  )}
                  {isDepositPaid && (
                    <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                      {t('managementAppointments.prepayment.balanceDueInStore')}
                    </Typography>
                  )}
                  {(paymentKey === 'refunded' || paymentKey === 'partiallyRefunded') && (
                    <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 600 }}>
                      {t(`managementAppointments.paymentStatus.${paymentKey}`)}
                    </Typography>
                  )}
                  {additionalCostAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                        {t('managementAppointments.additionalCost')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: BRAND.text }}>
                        {formatCurrency(additionalCostAmount, currencyCode)}
                      </Typography>
                    </Stack>
                  )}
                  {isPaid && paymentLines.length === 1 && (
                    <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                      {t(`storePaymentMethods.${getStorePaymentMethodKey(paymentLines[0].paymentMethod)}`)}
                    </Typography>
                  )}
                  {isPaid && paymentLines.length > 1 && paymentLines.map((line) => (
                    <Stack
                      key={line.paymentMethod}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                        {t(`storePaymentMethods.${getStorePaymentMethodKey(line.paymentMethod)}`)}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: BRAND.text }}>
                        {formatCurrency(line.amount, currencyCode)}
                      </Typography>
                    </Stack>
                  ))}
                  {isPaid ? (
                    <AppointmentInvoiceBar storeId={storeId} appointment={appointment} canIssue={canIssueInvoice} />
                  ) : null}
                  {membershipPlanLabel && isMembershipRelated && (
                    <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                      {pendingMembershipEnrollment
                        ? t('managementAppointments.membershipPriceNote', { plan: membershipPlanLabel })
                        : t('managementAppointments.membershipPlanNote', { plan: membershipPlanLabel })}
                    </Typography>
                  )}
                  {coveredByServicePackage && (
                    <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                      {servicePackageLabel
                        ? t('servicePackagesManagement.appointment.packageNote', { name: servicePackageLabel })
                        : t('servicePackagesManagement.appointment.coveredByPackage')}
                    </Typography>
                  )}
                </Stack>
              </DetailSection>
              )}
              {nothingToCollect && ((membershipPlanLabel && isMembershipRelated) || coveredByServicePackage) && (
                <Stack spacing={0.5} sx={{ px: 0.5 }}>
                  {membershipPlanLabel && isMembershipRelated && (
                    <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                      {pendingMembershipEnrollment
                        ? t('managementAppointments.membershipPriceNote', { plan: membershipPlanLabel })
                        : t('managementAppointments.membershipPlanNote', { plan: membershipPlanLabel })}
                    </Typography>
                  )}
                  {coveredByServicePackage && (
                    <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                      {servicePackageLabel
                        ? t('servicePackagesManagement.appointment.packageNote', { name: servicePackageLabel })
                        : t('servicePackagesManagement.appointment.coveredByPackage')}
                    </Typography>
                  )}
                </Stack>
              )}

              <DetailSection icon={NotesIcon} title={t('managementAppointments.additionalInfo')}>
                {customerNotes ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: BRAND.textMuted,
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                    }}
                  >
                    {customerNotes}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: BRAND.textSubtle, fontStyle: 'italic', lineHeight: 1.6 }}>
                    {t('managementAppointments.noAdditionalInfo')}
                  </Typography>
                )}
              </DetailSection>
            </Stack>
          )}
        </Box>

        {(showStatusActions || showOnRequestActions || hasOverflowActions) && (
          <Box
            sx={{
              flexShrink: 0,
              px: { xs: 2, sm: 3 },
              py: 2,
              borderTop: `1px solid ${BRAND.line}`,
              bgcolor: BRAND.surface,
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              {hasOverflowActions && (
                <>
                  <IconButton
                    onClick={handleMenuOpen}
                    disabled={isBusy}
                    aria-label={t('common.moreOptions')}
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      borderRadius: BRAND_RADIUS.button,
                      border: `1px solid ${BRAND.line}`,
                      color: BRAND.accentText,
                      bgcolor: BRAND.surface,
                      '&:hover': {
                        bgcolor: brandAlpha('accent', 0.06),
                        borderColor: BRAND.accent,
                      },
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>

                  <Menu
                    anchorEl={menuAnchor}
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    disableScrollLock
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    PaperProps={{
                      sx: {
                        mt: -1,
                        minWidth: 280,
                        maxWidth: 'min(360px, calc(100vw - 32px))',
                        borderRadius: 2,
                        boxShadow: `0 8px 24px ${brandAlpha('primary', 0.14)}`,
                      },
                    }}
                  >
                    {showEdit && (
                      <MenuItem onClick={() => handleMenuAction(onEdit)} disabled={isBusy} sx={overflowMenuItemSx}>
                        <ListItemIcon>
                          <EditIcon fontSize="small" sx={{ color: BRAND.accentText }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('managementAppointments.edit.action')}
                          primaryTypographyProps={{ variant: 'body2', noWrap: false, sx: { whiteSpace: 'normal' } }}
                        />
                      </MenuItem>
                    )}

                    {showChangeToRegular && (
                      <MenuItem onClick={() => handleMenuAction(onChangeToRegular)} disabled={isBusy} sx={overflowMenuItemSx}>
                        <ListItemIcon>
                          <SwapHorizIcon fontSize="small" sx={{ color: BRAND.accentText }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={changingToRegular
                            ? t('managementAppointments.changingToRegular')
                            : t('managementAppointments.changeToRegular')}
                          primaryTypographyProps={{ variant: 'body2', noWrap: false, sx: { whiteSpace: 'normal' } }}
                        />
                      </MenuItem>
                    )}

                    {showMarkPaid && (
                      <MenuItem onClick={() => handleMenuAction(onMarkPaid)} disabled={isBusy} sx={overflowMenuItemSx}>
                        <ListItemIcon>
                          <PaymentsIcon fontSize="small" sx={{ color: BRAND.accentText }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={markingPaid ? t('managementAppointments.markingPaid') : t('managementAppointments.markPaid')}
                          primaryTypographyProps={{ variant: 'body2', noWrap: false, sx: { whiteSpace: 'normal' } }}
                        />
                      </MenuItem>
                    )}

                    {showCancel && (
                      <MenuItem onClick={() => handleMenuAction(onMarkCancelled)} disabled={isBusy} sx={overflowMenuItemSx}>
                        <ListItemIcon>
                          <EventBusyIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={updatingStatus ? t('managementAppointments.cancelling') : t('managementAppointments.markCancelled')}
                          primaryTypographyProps={{ variant: 'body2', noWrap: false, sx: { whiteSpace: 'normal' } }}
                        />
                      </MenuItem>
                    )}

                    {showNoShow && (
                      <MenuItem onClick={() => handleMenuAction(onMarkNoShow)} disabled={isBusy} sx={overflowMenuItemSx}>
                        <ListItemIcon>
                          <PersonOffIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={updatingStatus ? t('managementAppointments.markingNoShow') : t('managementAppointments.markNoShow')}
                          primaryTypographyProps={{ variant: 'body2', noWrap: false, sx: { whiteSpace: 'normal' } }}
                        />
                      </MenuItem>
                    )}

                    {showDelete && (
                      <MenuItem onClick={() => handleMenuAction(onDelete)} disabled={isBusy} sx={{ ...overflowMenuItemSx, color: 'error.main' }}>
                        <ListItemIcon>
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('managementAppointments.delete.action')}
                          primaryTypographyProps={{ variant: 'body2', noWrap: false, sx: { whiteSpace: 'normal' } }}
                        />
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}

              {showOnRequestActions && (
                <>
                  <Button
                    color="inherit"
                    onClick={onDeclineOnRequest}
                    disabled={isBusy}
                    sx={{
                      ...footerActionButtonSx,
                      flex: 1,
                      border: `1px solid ${BRAND.line}`,
                      bgcolor: BRAND.surface,
                    }}
                  >
                    {updatingStatus ? t('managementAppointments.decliningOnRequest') : t('managementAppointments.declineOnRequest')}
                  </Button>
                  <PrimaryActionButton
                    onClick={onConfirmOnRequest}
                    disabled={isBusy}
                    sx={{
                      ...footerActionButtonSx,
                      flex: 1,
                      ...successActionButtonSx,
                    }}
                  >
                    {updatingStatus ? t('managementAppointments.confirmingOnRequest') : t('managementAppointments.confirmOnRequest')}
                  </PrimaryActionButton>
                </>
              )}

              {showStatusActions && (
                <PrimaryActionButton
                  onClick={onMarkCompleted}
                  disabled={isBusy}
                  sx={{
                    ...footerActionButtonSx,
                    flex: 1,
                    ...successActionButtonSx,
                  }}
                >
                  {updatingStatus ? t('managementAppointments.completing') : t('managementAppointments.markCompleted')}
                </PrimaryActionButton>
              )}
            </Stack>
          </Box>
        )}
      </Box>
      {pricingService && (
        <SetAppointmentServicePriceDialog
          open
          service={pricingService}
          currencyCode={currencyCode}
          onClose={() => setPricingService(null)}
          onSave={async (unitPrice) => {
            await onSetServicePrice?.(pricingService.serviceId, unitPrice);
            setPricingService(null);
          }}
        />
      )}
    </Drawer>
  );
};

export default AppointmentDetailsDrawer;
